import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import { FiCheck, FiAlertCircle, FiCamera, FiLayout, FiUser, FiGrid } from "react-icons/fi";

const STEPS = [
  { id: 1, label: "Name", icon: FiUser },
  { id: 2, label: "Logo", icon: FiCamera },
  { id: 3, label: "Categories", icon: FiGrid },
];

const OnboardingModal = ({ show, onHide }) => {
  const { t } = useTranslation();
  const { user, completeOnboarding } = useAuth();
  const [step, setStep] = useState(1);
  const [profileName, setProfileName] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (show && allCategories.length === 0) {
      setLoadingCategories(true);
      api
        .get("/categories/")
        .then((response) => setAllCategories(response.data.results || response.data))
        .catch(() => setError("Failed to load categories."))
        .finally(() => setLoadingCategories(false));
    }
  }, [show, allCategories.length]);

  useEffect(() => {
    if (!show) return;

    setStep(1);
    setProfileName(
      user?.supplier_profile?.company_name ||
        user?.buyer_profile?.full_name ||
        [user?.first_name, user?.last_name].filter(Boolean).join(" ")
    );
    setLogoFile(null);
    setSelectedCategories([]);
    setError("");
  }, [show, user]);

  const logoPreview = useMemo(() => {
    if (logoFile) return URL.createObjectURL(logoFile);
    return (
      user?.supplier_profile?.logo_url ||
      user?.buyer_profile?.logo_url ||
      user?.google_picture ||
      "/logo.png"
    );
  }, [logoFile, user]);

  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleCategoryToggle = (id) => {
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const validateStep = () => {
    if (step === 1 && !profileName.trim()) {
      setError("Store or profile name is required.");
      return false;
    }
    if (step === 3 && selectedCategories.length === 0) {
      setError("Select at least one category.");
      return false;
    }
    setError("");
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setStep((current) => Math.min(current + 1, STEPS.length));
  };

  const handleBack = () => {
    setError("");
    setStep((current) => Math.max(current - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    setLoading(true);
    setError("");

    const formData = new FormData();
    formData.append("account_type", "SUPPLIER");
    formData.append("company_name", profileName.trim());
    selectedCategories.forEach((id) => formData.append("industry_ids", id));
    if (logoFile) {
      formData.append("logo", logoFile);
    }

    try {
      const response = await api.post("/me/set-account-type/", formData);
      completeOnboarding(response.data);
      onHide();
    } catch (err) {
      console.error("Onboarding submission failed:", err.response || err);
      setError("Failed to complete setup. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[3000] bg-white animate-fade-in flex flex-col md:flex-row overflow-hidden">
      {/* Sidebar Content */}
      <div className="bg-theme-bg md:w-[400px] lg:w-[480px] shrink-0 border-r border-component-border p-8 md:p-12 flex flex-col justify-between overflow-y-auto">
        <div className="space-y-10">
          <img src="/logo.png" alt="Chafadi" className="h-8 w-fit" />
          
          <div className="space-y-4">
            <span className="text-chafadi-primary text-[10px] font-black uppercase tracking-[0.3em]">Welcome to Chafadi</span>
            <h2 className="text-3xl font-black text-theme-text leading-tight font-display tracking-tight uppercase">
              {t("onboardingPage.welcome", { name: user?.first_name || t("common.user") })}
            </h2>
            <p className="text-theme-muted font-bold text-[10px] leading-relaxed uppercase tracking-widest">
              Complete your profile in 3 simple steps to start trading on the marketplace.
            </p>
          </div>

          <div className="space-y-6 pt-4">
            {STEPS.map((item) => (
              <div key={item.id} className="flex items-center gap-6 group">
                <div className={`w-12 h-12 rounded-[18px] flex items-center justify-center text-lg transition-all ${
                  step === item.id 
                    ? "bg-chafadi-primary text-white shadow-premium-orange scale-110" 
                    : step > item.id 
                      ? "bg-emerald-500 text-white shadow-lg shadow-emerald-100" 
                      : "bg-white border border-component-border text-theme-muted opacity-40"
                }`}>
                  {step > item.id ? <FiCheck strokeWidth={4} /> : <item.icon />}
                </div>
                <div className="flex flex-col">
                  <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${step === item.id ? "text-theme-text" : "text-theme-muted opacity-50"}`}>
                    {item.label}
                  </span>
                  {step === item.id && (
                    <span className="text-[8px] font-black text-chafadi-primary uppercase tracking-[0.3em] animate-pulse">Loading...</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 p-8 bg-white rounded-[32px] border border-component-border flex items-center gap-6 shadow-2xl shadow-gray-200/50 animate-zoom-in group">
          <div className="relative">
             <img src={logoPreview} alt="Preview" className="w-16 h-16 rounded-2xl object-cover bg-theme-bg shrink-0 shadow-inner group-hover:scale-110 transition-transform duration-500" />
             <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-chafadi-primary rounded-full border-2 border-white flex items-center justify-center">
                <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
             </div>
          </div>
          <div className="min-w-0">
            <div className="font-black text-[10px] text-theme-text truncate uppercase tracking-widest">{profileName || "Unnamed Profile"}</div>
            <div className="text-[8px] font-bold text-theme-muted truncate uppercase tracking-[0.2em] mt-1">
              {selectedCategories.length > 0 ? `${selectedCategories.length} CATEGORIES` : "AWAITING SELECTION"}
            </div>
          </div>
        </div>
      </div>

      {/* Main Flow Panel */}
      <div className="flex-1 flex flex-col relative">
        <div className="flex-1 overflow-y-auto px-6 py-12 md:px-20 md:py-24 max-w-4xl mx-auto w-full">
          {error && (
            <div className="bg-rose-50 text-rose-500 p-5 rounded-3xl border border-rose-100 flex items-center gap-4 text-[9px] font-black uppercase tracking-widest mb-12 animate-shake">
              <FiAlertCircle className="text-lg shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="h-full flex flex-col justify-center">
            {step === 1 && (
              <div className="space-y-10 animate-fade-in">
                <div className="space-y-4">
                  <span className="inline-flex px-4 py-1.5 bg-chafadi-primary/10 text-chafadi-primary text-[10px] font-black tracking-[0.4em] rounded-full uppercase border border-chafadi-primary/20">Step 01</span>
                  <h3 className="text-5xl font-black text-theme-text tracking-tight font-display uppercase leading-tight">Profile Name</h3>
                  <p className="text-[10px] font-bold text-theme-muted uppercase tracking-widest leading-relaxed">Choose the name buyers will see for your store or profile.</p>
                </div>
                <div className="space-y-4">
                  <label className="text-[10px] font-black text-theme-muted uppercase tracking-[0.4em] ml-4">Establish Label</label>
                  <input
                    type="text"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    placeholder="E.G. NIGER LOGISTICS HUB"
                    className="w-full h-20 bg-theme-bg border border-component-border rounded-[32px] px-10 text-xl font-black text-theme-text placeholder:text-gray-300 focus:ring-8 focus:ring-chafadi-primary/10 outline-none transition-all uppercase tracking-widest shadow-inner"
                    required
                  />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-10 animate-fade-in">
                <div className="space-y-4">
                  <span className="inline-flex px-4 py-1.5 bg-chafadi-primary/10 text-chafadi-primary text-[10px] font-black tracking-[0.4em] rounded-full uppercase border border-chafadi-primary/20">Step 02</span>
                  <h3 className="text-5xl font-black text-theme-text tracking-tight font-display uppercase leading-tight">Add Logo</h3>
                  <p className="text-[10px] font-bold text-theme-muted uppercase tracking-widest leading-relaxed">A professional logo helps build trust with your customers. A square image works best.</p>
                </div>
                
                <div className="flex flex-col items-center gap-12 p-16 bg-theme-bg rounded-[60px] border border-component-border border-dashed relative group shadow-inner">
                  <div className="relative">
                    <img src={logoPreview} alt="Logo preview" className="w-56 h-56 rounded-[48px] shadow-3xl object-cover border-8 border-white group-hover:scale-[1.03] transition-all duration-700" />
                    <div className="absolute -bottom-4 -right-4 w-12 h-12 bg-chafadi-primary text-white rounded-[18px] flex items-center justify-center shadow-xl">
                       <FiCamera size={24} />
                    </div>
                  </div>
                  <div className="text-center space-y-6">
                    <div className="relative">
                      <input
                        type="file"
                        id="onboarding-logo"
                        onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                        accept="image/*"
                        className="sr-only"
                      />
                      <label 
                        htmlFor="onboarding-logo" 
                        className="bg-white px-12 h-16 rounded-full font-black text-[10px] text-theme-text border-2 border-component-border hover:bg-theme-text hover:text-white cursor-pointer transition-all shadow-xl flex items-center justify-center uppercase tracking-[0.3em] group/btn"
                      >
                         <FiLayout className="mr-3 group-hover/btn:rotate-6 transition-transform" />
                        {t("onboardingPage.yourLogoOptional")}
                      </label>
                    </div>
                    <span className="text-[9px] font-black text-theme-muted uppercase tracking-[0.4em] opacity-40">PNG, JPG, OR WEBP &bull; MAX 5MB</span>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-10 animate-fade-in h-full flex flex-col">
                <div className="space-y-4">
                  <span className="inline-flex px-4 py-1.5 bg-chafadi-primary/10 text-chafadi-primary text-[10px] font-black tracking-[0.4em] rounded-full uppercase border border-chafadi-primary/20">Step 03</span>
                  <h3 className="text-5xl font-black text-theme-text tracking-tight font-display uppercase leading-tight">Categories</h3>
                  <p className="text-[10px] font-bold text-theme-muted uppercase tracking-widest leading-relaxed">Select the industries you operate in to help buyers find you more easily.</p>
                </div>

                {loadingCategories ? (
                  <div className="flex-1 flex flex-col items-center justify-center gap-6">
                    <div className="w-12 h-12 border-4 border-chafadi-primary/20 border-t-chafadi-primary rounded-full animate-spin" />
                    <span className="text-[10px] font-black uppercase tracking-[0.5em] text-theme-muted animate-pulse">Loading...</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 overflow-y-auto pr-4 custom-scrollbar flex-1 max-h-[500px] py-4">
                    {allCategories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => handleCategoryToggle(cat.id)}
                        className={`p-6 rounded-[32px] text-[10px] font-black uppercase tracking-widest transition-all border text-center flex flex-col items-center justify-center gap-3 min-h-32 ${
                          selectedCategories.includes(cat.id)
                            ? "bg-theme-text border-theme-text text-white shadow-premium scale-[0.98]"
                            : "bg-white border-component-border text-theme-muted hover:border-chafadi-primary shadow-sm"
                        }`}
                      >
                        <FiGrid className={`text-lg ${selectedCategories.includes(cat.id) ? 'text-chafadi-primary' : 'text-gray-200'}`} />
                        {cat.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </form>
        </div>

        {/* Footer Navigation */}
        <div className="h-28 px-8 md:px-20 border-t border-component-border bg-white flex items-center justify-between shrink-0">
          <div>
            {step > 1 && (
              <button
                type="button"
                onClick={handleBack}
                className="px-8 h-14 rounded-full font-black text-[10px] text-theme-muted hover:bg-theme-bg transition-all uppercase tracking-[0.3em] flex items-center gap-3 pt-1"
              >
                Back
              </button>
            )}
          </div>

          <div>
            {step < STEPS.length ? (
              <button
                type="button"
                onClick={handleNext}
                className="px-12 h-16 bg-theme-text text-white font-black rounded-full shadow-2xl hover:bg-black transition-all text-xs uppercase tracking-[0.4em] flex items-center gap-4 pt-1"
              >
                Next
              </button>
            ) : (
              <button 
                type="button"
                onClick={handleSubmit} 
                disabled={loading} 
                className="px-12 h-20 bg-chafadi-primary text-white font-black rounded-full shadow-premium-orange hover:shadow-orange-500/40 transition-all text-sm uppercase tracking-[0.4em] flex items-center justify-center min-w-[240px] pt-1"
              >
                {loading ? <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin" /> : "Complete Setup"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
